import { nextTick, onBeforeUnmount, onMounted, ref, watch, type Ref, type WatchSource } from 'vue'

import type { DesktopNavKey } from '@/layout/partials/desktopNav'

type UseDesktopDropdownNavOptions = {
  headerInnerRef: Ref<HTMLElement | null>
  routePath: WatchSource<string>
}

export function useDesktopDropdownNav({
  headerInnerRef,
  routePath,
}: UseDesktopDropdownNavOptions) {
  const activeDesktopNav = ref<DesktopNavKey | null>(null)

  let closeTimer: number | null = null
  let openRequestId = 0

  function clearCloseTimer() {
    if (closeTimer === null || typeof window === 'undefined') return
    window.clearTimeout(closeTimer)
    closeTimer = null
  }

  async function setDesktopNav(key: DesktopNavKey) {
    clearCloseTimer()
    const requestId = ++openRequestId

    if (activeDesktopNav.value && activeDesktopNav.value !== key) {
      activeDesktopNav.value = null
      await nextTick()

      if (requestId !== openRequestId) return false
    }

    activeDesktopNav.value = key
    await nextTick()

    return requestId === openRequestId
  }

  function openDesktopNav(key: DesktopNavKey) {
    void setDesktopNav(key)
  }

  function closeDesktopNav() {
    clearCloseTimer()
    openRequestId += 1
    activeDesktopNav.value = null
  }

  function scheduleCloseDesktopNav() {
    clearCloseTimer()

    if (typeof window === 'undefined') {
      activeDesktopNav.value = null
      return
    }

    closeTimer = window.setTimeout(() => {
      activeDesktopNav.value = null
      closeTimer = null
    }, 140)
  }

  function toggleDesktopNav(key: DesktopNavKey) {
    if (activeDesktopNav.value === key) {
      closeDesktopNav()
      return
    }

    openDesktopNav(key)
  }

  async function focusDesktopNav(key: DesktopNavKey) {
    const opened = await setDesktopNav(key)
    if (!opened) return

    const linkTarget = document.getElementById(`header-${key}-overview-link`)

    if (linkTarget instanceof HTMLAnchorElement) {
      linkTarget.focus({ preventScroll: true })
    }
  }

  async function focusNextDesktopNavElement(key: DesktopNavKey | null) {
    if (!key) return

    const nextTargetId = key === 'gym'
      ? 'header-trainer-trigger'
      : 'header-memberships-trigger'

    closeDesktopNav()
    await nextTick()

    const nextTarget = document.getElementById(nextTargetId)

    if (nextTarget instanceof HTMLElement) {
      nextTarget.focus({ preventScroll: true })
    }
  }

  function focusDesktopNavTrigger(key: DesktopNavKey | null) {
    if (!key) return

    const triggerTarget = document.getElementById(`header-${key}-trigger`)

    if (triggerTarget instanceof HTMLAnchorElement) {
      triggerTarget.focus({ preventScroll: true })
    }
  }

  function handlePointerDown(event: PointerEvent) {
    if (!activeDesktopNav.value) return

    const target = event.target
    if (!(target instanceof Node)) return
    if (headerInnerRef.value?.contains(target)) return

    closeDesktopNav()
  }

  function handleEscape(event: KeyboardEvent) {
    if (event.key !== 'Escape' || !activeDesktopNav.value) return
    closeDesktopNav()
  }

  function handleFocusOut(event: FocusEvent) {
    const nextTarget = event.relatedTarget

    if (nextTarget instanceof Node && headerInnerRef.value?.contains(nextTarget)) {
      return
    }

    closeDesktopNav()
  }

  function closeDesktopNavAndFollowLink() {
    closeDesktopNav()
  }

  watch(routePath, () => closeDesktopNav())

  onMounted(() => {
    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleEscape)
  })

  onBeforeUnmount(() => {
    clearCloseTimer()
    document.removeEventListener('pointerdown', handlePointerDown)
    document.removeEventListener('keydown', handleEscape)
  })

  return {
    activeDesktopNav,
    clearCloseTimer,
    closeDesktopNav,
    closeDesktopNavAndFollowLink,
    focusDesktopNav,
    focusNextDesktopNavElement,
    focusDesktopNavTrigger,
    handleFocusOut,
    openDesktopNav,
    scheduleCloseDesktopNav,
    toggleDesktopNav,
  }
}
